import { z } from "zod";

const taskTypeSchema = z.enum(["call", "question"]);
const taskStatusSchema = z.enum(["todo", "waiting", "done"]);

const taskSchema = z.object({
  id: z.string(),
  doctorId: z.string(),
  assignedSecretaryId: z.string().nullable(),
  taskType: taskTypeSchema,
  status: taskStatusSchema,
  title: z.string(),
  description: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const taskStepSchema = z.object({
  id: z.string(),
  taskId: z.string(),
  authorId: z.string(),
  authorRole: z.enum(["doctor", "secretary"]),
  authorFullname: z.string(),
  kind: z.enum(["created", "updated", "comment"]),
  status: taskStatusSchema,
  assignedSecretaryId: z.string().nullable(),
  comment: z.string().nullable(),
  createdAt: z.string(),
});

const taskDetailSchema = z.object({
  task: taskSchema,
  steps: z.array(taskStepSchema),
});

const secretarySchema = z.object({
  id: z.string(),
  fullname: z.string(),
});

export type TaskType = z.infer<typeof taskTypeSchema>;
export type TaskStatus = z.infer<typeof taskStatusSchema>;
export type Task = z.infer<typeof taskSchema>;
export type TaskStep = z.infer<typeof taskStepSchema>;
export type TaskDetail = z.infer<typeof taskDetailSchema>;
export type TaskSecretary = z.infer<typeof secretarySchema>;

const tasksApiBaseUrl = "/cadendarTasks";

async function request(path: string, options?: RequestInit) {
  const response = await fetch(`${tasksApiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
  if (!response.ok) {
    if (response.status === 401) {
      window.location.reload();
    }
    const body = await response.json().catch(() => null);
    const message =
      typeof body === "object" && body !== null && "error" in body && typeof body.error === "string"
        ? body.error
        : "La requête vers les tâches a échoué.";
    throw new Error(message);
  }
  return response.json();
}

function doctorPath(doctorId: string) {
  return `/doctors/${encodeURIComponent(doctorId)}`;
}

function taskPath(taskId: string) {
  return `/tasks/${encodeURIComponent(taskId)}`;
}

export const tasksApi = {
  async listSecretaries(doctorId: string) {
    return z.array(secretarySchema).parse(await request(`${doctorPath(doctorId)}/secretaries`));
  },
  async listTasks(options: {
    doctorId: string;
    status?: TaskStatus;
    taskType?: TaskType;
    assignedSecretaryId?: string;
  }) {
    const parameters = new URLSearchParams();
    if (options.status) parameters.set("status", options.status);
    if (options.taskType) parameters.set("taskType", options.taskType);
    if (options.assignedSecretaryId) {
      parameters.set("assignedSecretaryId", options.assignedSecretaryId);
    }
    const query = parameters.size > 0 ? `?${parameters.toString()}` : "";
    return z.array(taskSchema).parse(await request(`${doctorPath(options.doctorId)}/tasks${query}`));
  },
  async getTask(taskId: string) {
    return taskDetailSchema.parse(await request(taskPath(taskId)));
  },
  async createTask(options: {
    doctorId: string;
    taskType: TaskType;
    title: string;
    description: string;
    assignedSecretaryId?: string;
    status: TaskStatus;
  }) {
    const { doctorId, ...body } = options;
    return taskSchema.parse(
      await request(`${doctorPath(doctorId)}/tasks`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    );
  },
  async updateTask(options: {
    taskId: string;
    expectedUpdatedAt: string;
    taskType?: TaskType;
    title?: string;
    description?: string;
  }) {
    const { taskId, ...body } = options;
    return taskSchema.parse(
      await request(taskPath(taskId), {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    );
  },
  async addStep(options: {
    taskId: string;
    expectedUpdatedAt: string;
    status?: TaskStatus;
    assignedSecretaryId?: string;
    unassign?: boolean;
    comment?: string;
  }) {
    const { taskId, ...body } = options;
    return taskSchema.parse(
      await request(`${taskPath(taskId)}/steps`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    );
  },
};
